"use client";
import { HTMLAttributes, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/custom/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import api from "@/app/api";
import { AxiosError } from "axios";
import { useRouter } from "next/navigation";
import { OtpForm } from "./otp-form";
import useAuthStore from "@/lib/useAuthStore";

interface OtpAuthFormProps extends HTMLAttributes<HTMLDivElement> {
  email?: string;
  onEmailChange?: (email: string) => void;
}

const requestOtpSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Please Enter Your Email" })
    .email({ message: "Invalid Email Address" }),
});

interface OtpRequestResponse {
  success: boolean;
  message: string;
  otp_token: string;
}

interface OtpVerifyResponse {
  success: boolean;
  message: string;
  access_token: string;
}

export function OtpAuthForm({
  className,
  email,
  onEmailChange,
  ...props
}: OtpAuthFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const router = useRouter();

  const requestForm = useForm<z.infer<typeof requestOtpSchema>>({
    resolver: zodResolver(requestOtpSchema),
    defaultValues: {
      email: email || "",
    },
  });

  // Keep email in sync when switching from password tab
  useEffect(() => {
    if (typeof email === "string") {
      requestForm.setValue("email", email);
    }
  }, [email, requestForm]);

  async function onRequestOtp(data: z.infer<typeof requestOtpSchema>) {
    setIsLoading(true);
    try {
      const response = await api.post<OtpRequestResponse>("/auth/otp-login/request", data);
      if (response.data.success) {
        setOtpToken(response.data.otp_token);
        setOtpSent(true);
        onEmailChange?.(data.email);
        toast.success("OTP Sent To Your Email");
      }
    } catch (error: any) {
      const errorMessage =
        error instanceof AxiosError
          ? error.response?.data?.message || "Failed To Send OTP"
          : "An Unexpected Error Occurred";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerifyOtp(otp: string) {
    try {
      const response = await api.post<OtpVerifyResponse>(
        "/auth/otp-login/verify",
        {
          otp_token: otpToken,
          otp: otp,
        },
        {
          withCredentials: true
        }
      );

      if (response.data.success) {
        // Store token in localStorage
        localStorage.setItem("access_token", response.data.access_token);
        
        // Update auth state in auth store (need to manually set)
        const { setAuthenticationStatus } = useAuthStore.getState();
        await setAuthenticationStatus(true);
        
        // Get user data to complete auth state
        try {
          await useAuthStore.getState().getUser();
        } catch (userError) {
          console.error("Failed to get user after OTP login:", userError);
        }
        
        toast.success("Login Successful");
        
        // Safe redirect with timeout to ensure state is updated
        setTimeout(() => {
          router.push("/flight");
        }, 100);
        
        return response;
      }
      throw new Error("Verification failed");
    } catch (error: any) {
      const errorMessage =
        error instanceof AxiosError
          ? error.response?.data?.message || "Invalid OTP"
          : "An Unexpected Error Occurred";
      toast.error(errorMessage);
      throw error;
    }
  }

  const handleResendOtp = async () => {
    const data = requestForm.getValues();
    await onRequestOtp(data);
  };

  const currentEmail = requestForm.watch("email");

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <Form {...requestForm}>
        <form onSubmit={requestForm.handleSubmit(onRequestOtp)}>
          <div className="grid gap-2">
            <FormField
              control={requestForm.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Enter Email Address" 
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          onEmailChange?.(e.target.value);
                        }}
                        disabled={otpSent}
                        className="flex-1"
                      />
                      {!otpSent && (
                        <Button
                          type="submit"
                          className="bg-[#022f5d] hover:bg-[#02305dcc] text-white"
                          loading={isLoading}
                        >
                          Send OTP
                        </Button>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>

      {otpSent && (
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground flex items-center justify-between gap-2">
            <span>
              Enter The OTP Sent To{" "}
              <span className="font-medium text-foreground">{currentEmail}</span>
            </span>
            <Button
              variant="link"
              className="p-0 h-auto text-[#022f5d] shrink-0"
              onClick={handleResendOtp}
              disabled={isLoading}
            >
              Resend OTP
            </Button>
          </div>
          <OtpForm VerifyOTP={handleVerifyOtp} />
        </div>
      )}
    </div>
  );
}
